/**
 * Display wording for notifications.
 *
 * New notifications are written in plain words by the server. Rows written
 * before that — and anything seeded — still say "2026-09-15" and "4.00
 * day(s)", and the category is the dotted type code. These helpers make the
 * old rows read like the new ones; they never change what is stored.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The first segment of a type code, as the group a reader recognises. */
const CATEGORY_LABELS: Record<string, string> = {
  announcement: 'Announcement',
  attendance: 'Attendance',
  auth: 'Security',
  security: 'Security',
  device: 'Devices',
  export: 'Exports',
  leave: 'Leave',
  overtime: 'Overtime',
  payroll: 'Payroll',
  payslip: 'Payslip',
  performance: 'Performance',
  policy: 'Policy',
  probation: 'Probation',
  recruitment: 'Recruitment',
  training: 'Learning',
  users: 'Users',
  simulation: 'Simulation',
};

function sentenceCase(value: string): string {
  const words = value
    .replace(/[._-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * `attendance.review.approved` → "Attendance". The badge names the area a
 * notification is about; the title already says what happened.
 */
export function notificationCategoryLabel(type: string): string {
  const head = type.split('.')[0] ?? type;
  return CATEGORY_LABELS[head] ?? (sentenceCase(head) || 'General');
}

/** A string that is nothing but a type code, e.g. `leave.request.approved`. */
const CODE_PATTERN = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/;

/**
 * Plain wording for text that may predate it.
 *
 * - ISO dates become "15 Sep 2026" (read as calendar dates, not instants, so
 *   no time zone can move them);
 * - "4.00 day(s)" becomes "4 days";
 * - a bare type code becomes a sentence.
 */
export function humanizeNotificationText(text: string): string {
  if (!text) return text;
  if (CODE_PATTERN.test(text.trim())) return sentenceCase(text);

  return (
    text
      .replace(
        /\b(\d{4})-(\d{2})-(\d{2})(?![\d:T])/g,
        (match, year: string, month: string, day: string) => {
          const monthIndex = Number(month) - 1;
          if (monthIndex < 0 || monthIndex > 11) return match;
          return `${Number(day)} ${MONTHS[monthIndex]} ${year}`;
        },
      )
      // A one-day range written out twice reads as a typo.
      .replace(/\b(\d{1,2} [A-Z][a-z]{2} \d{4}) to \1\b/g, '$1')
      .replace(
        /\b(\d+(?:\.\d+)?) (day|hour)\(s\)( leave| overtime)?/g,
        (_match, amount: string, unit: string, what: string | undefined) => {
          const value = Number(amount);
          return `${value} ${unit}${value === 1 ? '' : 's'}${what ? ` of${what}` : ''}`;
        },
      )
  );
}

/** Payroll period statuses as the second half of a sentence about the period. */
const PERIOD_STATUS_PHRASES: Record<string, (label: string) => string> = {
  attendance_review: (label) => `${label} is in attendance review`,
  input_review: (label) => `${label} is in input review`,
  calculated: (label) => `${label} has been calculated`,
  for_approval: (label) => `${label} is waiting for approval`,
  approved: (label) => `${label} has been approved`,
  locked: (label) => `${label} is locked`,
  payslips_published: (label) => `Payslips for ${label} are published`,
  completed: (label) => `${label} is complete`,
};

/**
 * A notification's title, worded.
 *
 * Payroll-period notices were once titled "<period> is <status>", which read
 * "August 2026 is payslips published". The period label is recovered from
 * the stored title and the sentence rebuilt from the type's status.
 */
export function notificationTitle(type: string, title: string): string {
  const status = type.startsWith('payroll.period.') ? type.slice('payroll.period.'.length) : null;
  const phrase = status ? PERIOD_STATUS_PHRASES[status] : undefined;
  const label = phrase ? /^(.+?) is [a-z ]+$/.exec(title)?.[1] : undefined;
  if (phrase && label) return phrase(label);
  return humanizeNotificationText(title);
}
